import React, { useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { useQuery, useMutation } from '@apollo/client'
import {
  Table,
  Button,
  Collapse,
  List,
  Typography,
  Popconfirm,
  Row,
  Col,
  notification,
  Popover,
} from 'antd'
import {
  AlignLeftOutlined,
  EditOutlined,
  DeleteOutlined,
} from '@ant-design/icons'
import { Panel } from '../../../core/components/styled'
import { map } from 'lodash-es'
import {
  createUrl,
  getQueryParams,
} from '../../../core/utils/services/queryParams'
import { DEFAULT_PAGE_SIZE, DEFAULT_PAGE } from '../../../common/constants'
import { useDocumentTitle } from '../../../common/hooks/effects'
import { formatScrapingTags } from '../../utils'
import { SCRAPING_SESSION_TEMPLATES } from '../../graphql/queries'
import { DELETE_SCRAPING_SESSION_TEMPLATE } from '../../graphql/mutations'
import {
  ScrapingSessionTemplateEditorModal,
  ScrapingSessionPreviewModal,
  ScrapingTagsFilter,
  AliasFilter,
} from './sections'

const ScrapingSessionsManagementPage = () => {
  useDocumentTitle('Scraping session templates')

  const [
    scrapingSessionTemplateEditorModalSettings,
    setScrapingSessionTemplateEditorModalSettings,
  ] = useState({
    scrapingSessionTemplate: null,
    isModalOpen: false,
  })

  const [
    scrapingSessionPreviewModalSettings,
    setScrapingSessionPreviewModalSettings,
  ] = useState({
    scrapingSessionTemplate: null,
    isModalOpen: false,
  })

  const history = useHistory()
  const { pathname, search } = useLocation()
  const { tagIds, alias, page = DEFAULT_PAGE } = getQueryParams(search)

  const {
    data: { scrapingSessionTemplates = {} } = {},
    loading: isLoadingScrapingSessionTemplates,
    refetch: refetchScrapingSessionTemplates,
  } = useQuery(SCRAPING_SESSION_TEMPLATES, {
    variables: {
      page,
      filters: { tagIds, alias: alias?.toString() },
    },
    fetchPolicy: 'no-cache',
  })

  const [
    deleteScrapingSessionTemplate,
    { loading: isDeletingScrapingSessionTemplate },
  ] = useMutation(DELETE_SCRAPING_SESSION_TEMPLATE, {
    onCompleted() {
      refetchScrapingSessionTemplates()
      notification.open({
        message: 'Session template deleted.',
        type: 'success',
      })
    },
    onError({ message }) {
      notification.open({
        message: 'Failed to delete session template.',
        description: message,
        type: 'error',
        duration: 0,
      })
    },
  })

  const tableColumns = [
    {
      title: 'ID',
      dataIndex: 'id',
      width: '50px',
    },
    {
      title: 'Name',
      dataIndex: 'name',
    },
    {
      title: 'Alias',
      dataIndex: 'alias',
      width: '150px',
    },
    {
      title: 'Description',
      dataIndex: 'description',
      width: '250px',
    },
    {
      title: 'Test cases',
      dataIndex: 'scrapingTestCaseCount',
      width: '100px',
    },
    {
      title: 'Tag groups',
      dataIndex: 'tagGroups',
      width: '400px',
      render: tagGroups =>
        map(tagGroups, (tagGroup, index) => (
          <Collapse key={index} defaultActiveKey={['1']}>
            <Collapse.Panel header={tagGroup.alias || tagGroup.name}>
              <List
                size="small"
                split={false}
                dataSource={formatScrapingTags(tagGroup.tags)}
                itemLayout="horizontal"
                renderItem={tag => (
                  <List.Item style={{ padding: 0 }}>
                    <Typography.Text>{tag}</Typography.Text>
                  </List.Item>
                )}
              />
            </Collapse.Panel>
          </Collapse>
        )),
    },
    {
      title: () => (
        <Button
          type="primary"
          onClick={() =>
            setScrapingSessionTemplateEditorModalSettings({
              scrapingSessionTemplate: null,
              isModalOpen: true,
            })
          }
        >
          Create session template
        </Button>
      ),
      align: 'right',
      render: scrapingSessionTemplate => (
        <>
          <Popover
            trigger="hover"
            placement="topLeft"
            content="Preview template"
          >
            <Button
              style={{ marginRight: '15px' }}
              icon={<AlignLeftOutlined />}
              type="primary"
              onClick={() =>
                setScrapingSessionPreviewModalSettings({
                  scrapingSessionTemplate,
                  isModalOpen: true,
                })
              }
            />
          </Popover>
          <Popover trigger="hover" content="Edit template">
            <Button
              style={{ marginRight: '15px' }}
              icon={<EditOutlined />}
              onClick={() =>
                setScrapingSessionTemplateEditorModalSettings({
                  scrapingSessionTemplate,
                  isModalOpen: true,
                })
              }
            />
          </Popover>

          <Popconfirm
            title="Are you sure you want to delete this session template?"
            onConfirm={() =>
              deleteScrapingSessionTemplate({
                variables: { id: scrapingSessionTemplate.id },
              })
            }
            okText="Yes"
            cancelText="No"
          >
            <Popover
              trigger="hover"
              placement="topRight"
              content="Delete template"
            >
              <Button icon={<DeleteOutlined />} />
            </Popover>
          </Popconfirm>
        </>
      ),
    },
  ]

  return (
    <>
      <Panel>
        <Row>
          <Col>
            <ScrapingTagsFilter />
          </Col>
          <Col>
            <AliasFilter />
          </Col>
        </Row>
      </Panel>
      <Table
        loading={
          isLoadingScrapingSessionTemplates || isDeletingScrapingSessionTemplate
        }
        rowKey="id"
        columns={tableColumns}
        dataSource={scrapingSessionTemplates.scrapingSessionTemplates}
        size="small"
        pagination={{
          current: page,
          pageSize: DEFAULT_PAGE_SIZE,
          total: scrapingSessionTemplates.total,
          onChange: page => history.push(createUrl(pathname, search, { page })),
        }}
      />

      <ScrapingSessionPreviewModal
        {...scrapingSessionPreviewModalSettings}
        closeModal={() =>
          setScrapingSessionPreviewModalSettings({
            scrapingSesssionTemplate: null,
            isModalOpen: false,
          })
        }
      />

      <ScrapingSessionTemplateEditorModal
        {...scrapingSessionTemplateEditorModalSettings}
        refetchScrapingSessionTemplates={refetchScrapingSessionTemplates}
        closeModal={() =>
          setScrapingSessionTemplateEditorModalSettings({
            scrapingSesssionTemplate: undefined,
            isModalOpen: false,
          })
        }
      />
    </>
  )
}

export default ScrapingSessionsManagementPage
